import React, { createContext, ReactNode, useContext, useState } from 'react';
import { UIRegionOfInterest } from './steps/regionOfInterestSelection/UIRegionOfInterest';
import { ViewType } from './steps/viewType';
import { ScheduleType } from '../../shared/src/data/scheduleType';

type SelectedColumnsContextType = {
  pageToSelectedColumns: Map<number, Map<string, UIRegionOfInterest>>;
  setPageSelectedColumns: (page: number, regions: UIRegionOfInterest[]) => void;
  addRegionToPage: (page: number, region: UIRegionOfInterest) => void;
  removeRegionFromPage: (page: number, regionId: string) => void;
  setPageToSelectedColumnsMap: (newMap: Map<number, Map<string, UIRegionOfInterest>>) => void;
  getRegionsForPage: (page: number) => UIRegionOfInterest[] | undefined;
  updateRegionOfInterest: (
    page: number,
    regionId: string,
    newContentTypes: ViewType[],
    newLabel: ScheduleType,
  ) => void;
};

const SelectedColumnsContext = createContext<SelectedColumnsContextType | undefined>(undefined);

export const SelectedColumnsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pageToSelectedColumns, setPageToSelectedColumns] =
    useState<Map<number, Map<string, UIRegionOfInterest>>>(new Map());

  const addRegionToPage = (page: number, region: UIRegionOfInterest) => {
    setPageToSelectedColumns((prevMap) => {
      const newMap = new Map(prevMap);
      const existingRegions = newMap.get(page) || new Map();
      existingRegions.set(region.id, region);
      newMap.set(page, existingRegions);
      return newMap;
    });
  };

  const removeRegionFromPage = (page: number, regionId: string) => {
    setPageToSelectedColumns((prevMap) => {
      const newMap = new Map(prevMap);
      const pageRegions = newMap.get(page);

      if (pageRegions && pageRegions.has(regionId)) {
        pageRegions.delete(regionId);
      }
      if (pageRegions && pageRegions.size === 0) {
        newMap.delete(page);
      }

      return newMap;
    });
  }

  // Function to set regions for a specific page using a list of UIRegionOfInterest
  const setPageSelectedColumns = (page: number, regions: UIRegionOfInterest[]) => {
    const regionsMap = new Map<string, UIRegionOfInterest>();
    
    regions.forEach((region) => {
      regionsMap.set(region.id, region);
    });
    
    setPageToSelectedColumns((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(page, regionsMap);
      return newMap;
    });
  };

  const getRegionsForPage = (page: number): UIRegionOfInterest[] | undefined => {
    const pageRegions = pageToSelectedColumns.get(page);
    if (pageRegions) {
      return Array.from(pageRegions.values());
    }
    return undefined;
  };

  const updateRegionOfInterest = (
    page: number,
    regionId: string,
    newContentTypes: ViewType[],
    newLabel: ScheduleType,
  ) => {
    setPageToSelectedColumns((prevMap) => {
      const newMap = new Map(prevMap);
      const pageRegions = newMap.get(page);

      if (pageRegions && pageRegions.has(regionId)) {
        const region = pageRegions.get(regionId);
        if (region) {
          // Update the content type (assuming `contentType` is a field in UIRegionOfInterest)
          const updatedRegion = { ...region, regionContentType: newContentTypes, label: newLabel };
          pageRegions.set(regionId, updatedRegion);
        }
      }

      return newMap;
    });
  };

  const setPageToSelectedColumnsMap = (newMap: Map<number, Map<string, UIRegionOfInterest>>) => {
    setPageToSelectedColumns(newMap);
  };

  return (
    <SelectedColumnsContext.Provider
      value={{
        pageToSelectedColumns,
        setPageSelectedColumns,
        addRegionToPage,
        removeRegionFromPage,
        setPageToSelectedColumnsMap,
        getRegionsForPage,
        updateRegionOfInterest,
      }}
    >
      {children}
    </SelectedColumnsContext.Provider>
  );
};

export const useSelectedColumnsContext = () => {
  const context = useContext(SelectedColumnsContext);
  if (!context) {
    throw new Error('useSelectedColumns must be used within a SelectedColumnsProvider');
  }
  return context;
};